import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Cookies from 'js-cookie';
import {
  boardsAPI,
  imagesAPI,
  labelsAPI,
  authAPI,
  onboardingAPI,
} from '../../api';
import ImageLightbox from '../../components/ImageLightbox';
import LibraryModal from '../../components/LibraryModal';
import ExportPDFModal from '../../components/ExportPDFModal';
import ExportProgressOverlay from '../../components/ExportProgressOverlay';
import OwnerNotesSection from '../../components/OwnerNotesSection';
import ManageLabelsModal from '../../components/ManageLabelsModal';
import DocumentsSection from '../../components/DocumentsSection';
import DemoSpotlight from '../../components/DemoSpotlight';
import ArchiveModeBanner from '../../components/ArchiveModeBanner';
import DuplicateConfirmModal from '../../components/DuplicateConfirmModal';
import BatchUploadStatus from '../../components/BatchUploadStatus';
import { useBatchUpload } from '../../hooks/useBatchUpload';
import { getTranslation } from '../../i18n';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import BoardTopbar from './components/BoardTopbar';
import BoardAddImagesPanel from './components/BoardAddImagesPanel';
import BoardImageGrid from './components/BoardImageGrid';
import BoardSettingsPanel from './components/BoardSettingsPanel';
import PrivateSpace from './components/PrivateSpace';
import {
  DeleteBoardModal,
  DeletedBoardConfirmation,
  ExtendBoardModal,
  DownloadReadyModal,
} from './components/BoardEditorModals';
import { parseExternalUrls } from './utils/externalUrls';
import { copyToClipboard } from './utils/clipboard';
import { getReactionIcon } from './utils/reactions';
import { sha256Hex } from './utils/hash';

const DEFAULT_LABELS = ['Mood', 'Light', 'Pose', 'Wardrobe', 'Atmosphere'];

/**
 * BoardEditor — the studio-facing workspace for a single moodboard.
 *
 * This file is the orchestrator: it owns all editor state and handlers, and
 * composes the actual UI from small focused sub-components in `./components/`.
 *
 * Refactor notes (Phase 1 architecture-hygiene sprint, 2026-02):
 *   - All state and handlers stay here so there is a single source of truth.
 *   - Sub-components are purely presentational — they receive props and
 *     render JSX, nothing more. No state of their own, no API calls.
 *   - This avoids the stale-closure / cross-module hook risks that motivated
 *     the "only fix HIGH-RISK hook issues" rule from the P0 sprint.
 *   - The legacy entry point `pages/BoardEditor.js` is now a thin re-export
 *     of this module so all existing imports keep working.
 */
const BoardEditor = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  document.title = 'Maestra - Board Editor';

  // ── Core data ──────────────────────────────────────────────────────────
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Loading / progress flags ───────────────────────────────────────────
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  // Calm staged-progress overlay shown over the editor while either
  // export runs. 'idle' | 'running' | 'done' | 'failed'. The overlay
  // self-closes on `done` once the parent dismisses it; `failed` keeps
  // the panel open until the user acknowledges so the error never
  // disappears too quickly.
  const [exportProgress, setExportProgress] = useState('idle');

  // ── UI state ───────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // Client View editing now happens INSIDE the real client page —
  // "Customize Client View" navigates to /board/{token}?edit=1 where
  // every visible text is click-to-edit (see ClientView.js).
  const [selectedImage, setSelectedImage] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [isClientLinkOpen, setIsClientLinkOpen] = useState(false);
  const [isAddImagesOpen, setIsAddImagesOpen] = useState(false);
  const shareLinkInputRef = useRef(null);

  // Modals
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [inspirationLightboxIndex, setInspirationLightboxIndex] = useState(null);
  const [showDeleteBoardModal, setShowDeleteBoardModal] = useState(false);
  const [deletedBoardConfirmation, setDeletedBoardConfirmation] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);

  // Grid controls
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [sectionLabels, setSectionLabels] = useState(DEFAULT_LABELS);
  const [reorderMode, setReorderMode] = useState(false);
  const [filterFinalOnly, setFilterFinalOnly] = useState(false);

  // Branding / language / template-controlled features
  const [workspaceLang, setWorkspaceLang] = useState('en');
  const [brandName, setBrandName] = useState('');
  const [reactionMode, setReactionMode] = useState('mood');

  // External URL flow
  const [showExternalUrlInput, setShowExternalUrlInput] = useState(false);
  const [externalUrlText, setExternalUrlText] = useState('');
  const [parsedUrls, setParsedUrls] = useState([]);
  const [addingExternal, setAddingExternal] = useState(false);
  const [externalSaveToLibrary, setExternalSaveToLibrary] = useState(false);
  const [addProgress, setAddProgress] = useState({ current: 0, total: 0 });

  // Extend modal
  const [extendDays, setExtendDays] = useState(14);
  const [extending, setExtending] = useState(false);

  // Duplicate-confirmation modal — surfaced from any of the three add paths
  // (file drop, library pick, external URL). Items are duplicates only;
  // uniques are queued in `proceed` and committed when the user resolves.
  const [dupModal, setDupModal] = useState(null); // { items, uniqueCount, proceed }

  // Plan / archive state
  const [archiveMode, setArchiveMode] = useState(null);
  const [boardArchiveStatus, setBoardArchiveStatus] = useState(null);
  const [featureAccess, setFeatureAccess] = useState({
    allow_branding: false,
    allow_custom_texts: false,
    allow_image_conversations: true,
    allow_shared_decision: false,
    allow_final_decision_mode: false,
    allow_client_notifications: true,
    allow_notes: true,
    allow_client_uploads: true,
    allow_documents: true,
    allow_custom_message: false,
    allow_custom_welcome: false,
    allow_board_cover: false,
    allow_client_view_override: false,
  });

  // Download-ready state: {url, filename, type, token, size}
  const [downloadReady, setDownloadReady] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Demo spotlight
  // eslint-disable-next-line no-unused-vars
  const [dismissedGuidance, setDismissedGuidance] = useState([]);
  const [spotlightActive, setSpotlightActive] = useState(false);

  // ── DnD sensors (only active in reorder mode) ──────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── Effects ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchBoard();
    fetchLabels();
    fetchWorkspaceLang();
    onboardingAPI.state()
      .then((res) => {
        const dg = res.data.dismissed_guidance || [];
        setDismissedGuidance(dg);
        const previewMode = typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('preview_onboarding') === '1';
        if (!previewMode && !dg.includes('demo_spotlight')) {
          setTimeout(() => setSpotlightActive(true), 700);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  // (Custom Client Message removed from the UI entirely — legacy
  // `custom_client_message` field on the board doc is now dormant.)

  // ── Data fetchers ──────────────────────────────────────────────────────
  const fetchWorkspaceLang = async () => {
    try {
      const { data } = await authAPI.getSettings();
      setWorkspaceLang(data.language || 'en');
      if (data.archive_mode) {
        setArchiveMode(data.archive_mode);
        if (data.archive_mode.active && boardId) {
          boardsAPI.archiveExportStatus(boardId)
            .then(({ data: s }) => setBoardArchiveStatus(s))
            .catch(() => {});
        }
      }
      if (data.feature_access) setFeatureAccess(data.feature_access);
      if (data.reaction_mode) setReactionMode(data.reaction_mode);
      if (data.branding?.workspace_name) setBrandName(data.branding.workspace_name);
    } catch (err) { /* default en */ }
  };

  const fetchLabels = async () => {
    try {
      const { data } = await labelsAPI.get();
      if (data.labels && data.labels.length > 0) {
        setSectionLabels(data.labels);
      }
    } catch (err) {
      console.error('Error fetching labels:', err);
    }
  };

  const fetchBoard = async () => {
    try {
      const { data } = await boardsAPI.get(boardId);
      setBoard(data);
      setIsAddImagesOpen((prev) => prev || (data.images?.length || 0) === 0);
    } catch (err) {
      console.error('Error fetching board:', err);
      navigate('/boards');
    } finally {
      setLoading(false);
    }
  };

  // ── Mutations ──────────────────────────────────────────────────────────
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  // ── Upload commit helper ───────────────────────────────────────────────
  // Uses the shared batch uploader: bounded concurrency (5), per-file
  // status, one auto-retry, calm progress + summary card. Replaces the
  // older fully-sequential for-loop that swallowed failures into toasts
  // and never told the user how many succeeded vs failed.
  // Captured at batch-start time so every file in this batch uses the
  // same base. The hook's per-file `index` is added to this to produce
  // the absolute `selection_index` sent to the backend — eliminates the
  // concurrent-lookup race that scrambled the photographic sequence.
  const uploadBaseOrderRef = useRef(0);

  const uploadBatch = useBatchUpload({
    uploadFn: async (file, index) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('position_x', 0);
      formData.append('position_y', 0);
      formData.append('save_to_library', saveToLibrary);
      formData.append('selection_index', String(uploadBaseOrderRef.current + index));
      const res = await imagesAPI.upload(boardId, formData);
      return res?.data;
    },
    concurrency: 5,
    onAllDone: ({ done }) => {
      fetchBoard();
      if (done > 0) setIsAddImagesOpen(false);
    },
  });

  const commitFileUploads = useCallback(async (files, { silentToastOnEmpty = false } = {}) => {
    if (!files || files.length === 0) {
      if (!silentToastOnEmpty) showToast('Nothing to add');
      return;
    }
    // Compute the base order ONCE so the whole batch shares one anchor.
    // Existing images are returned by /api/boards/{id} sorted ASC by
    // `order`, so the last entry's order + 1 is where new files start.
    const existing = board?.images || [];
    const lastOrder = existing.length
      ? (existing[existing.length - 1].order ?? existing.length - 1)
      : -1;
    uploadBaseOrderRef.current = lastOrder + 1;
    await uploadBatch.start(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadBatch, board]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    // Pre-flight duplicate check — never blocks, just lets the studio confirm.
    let hashByFile = new Map();
    try {
      const entries = await Promise.all(
        acceptedFiles.map(async (f) => [f, await sha256Hex(f)])
      );
      hashByFile = new Map(entries);
      const hashes = entries.map(([, h]) => h);
      const { data: dup } = await imagesAPI.checkDuplicates(boardId, { content_hashes: hashes });

      const dupItems = [];
      const uniqueFiles = [];
      for (const [file, hash] of entries) {
        const existing = dup?.by_hash?.[hash];
        if (existing) {
          dupItems.push({
            key: hash + '::' + file.name,
            label: file.name,
            file,
            existing,
          });
        } else {
          uniqueFiles.push(file);
        }
      }

      if (dupItems.length > 0) {
        setDupModal({
          items: dupItems,
          uniqueCount: uniqueFiles.length,
          proceed: async ({ addKeys, skipKeys }) => {
            const addSet = new Set(addKeys || []);
            const extras = dupItems.filter((d) => addSet.has(d.key)).map((d) => d.file);
            await commitFileUploads([...uniqueFiles, ...extras], { silentToastOnEmpty: true });
            const skipped = (skipKeys || []).length;
            if (skipped > 0 && extras.length === 0 && uniqueFiles.length === 0) {
              showToast(`Skipped ${skipped} duplicate${skipped > 1 ? 's' : ''}`);
            }
          },
        });
        return;
      }
    } catch (err) {
      // Network/permission issues should never block uploading. Fall through
      // to the unchecked path so the studio can still add images.
      console.warn('Duplicate pre-check failed, continuing without it:', err);
      void hashByFile;
    }

    await commitFileUploads(acceptedFiles, { silentToastOnEmpty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, saveToLibrary, commitFileUploads]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = board.images.findIndex((img) => img.id === active.id);
    const newIndex = board.images.findIndex((img) => img.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newImages = arrayMove(board.images, oldIndex, newIndex);
    setBoard((prev) => ({ ...prev, images: newImages }));

    try {
      const imageIds = newImages.map((img) => img.id);
      await imagesAPI.reorder(boardId, imageIds);
    } catch (err) {
      console.error('Error reordering images:', err);
      fetchBoard();
      showToast('Failed to reorder images');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board?.images, boardId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic', '.heif'] },
  });

  const handleDeleteImage = async (imageId) => {
    try {
      await imagesAPI.delete(boardId, imageId);
      fetchBoard();
      setSelectedImage(null);
      showToast('Image deleted');
    } catch (err) {
      console.error('Error deleting image:', err);
    }
  };

  // External URL panel
  const handleExternalUrlTextChange = (e) => {
    const text = e.target.value;
    setExternalUrlText(text);
    setParsedUrls(text.trim() ? parseExternalUrls(text) : []);
  };

  const resetExternalUrlInput = () => {
    setShowExternalUrlInput(false);
    setExternalUrlText('');
    setParsedUrls([]);
    setExternalSaveToLibrary(false);
    setAddProgress({ current: 0, total: 0 });
  };

  // ── External URL commit helper ─────────────────────────────────────────
  const commitExternalUrlAdds = useCallback(async (urls) => {
    if (!urls || urls.length === 0) return { success: 0, fail: 0 };
    setAddingExternal(true);
    setAddProgress({ current: 0, total: urls.length });
    let successCount = 0;
    let failCount = 0;
    // Pre-compute base order once so the pasted-URL sequence is stable.
    const existing = board?.images || [];
    const base = existing.length
      ? ((existing[existing.length - 1].order ?? existing.length - 1) + 1)
      : 0;
    for (let i = 0; i < urls.length; i++) {
      setAddProgress({ current: i + 1, total: urls.length });
      try {
        await imagesAPI.addExternal(boardId, urls[i], null, null, externalSaveToLibrary, base + i);
        successCount++;
      } catch (err) {
        console.error(`Error adding ${urls[i]}:`, err);
        failCount++;
      }
    }
    setAddingExternal(false);
    setAddProgress({ current: 0, total: 0 });
    fetchBoard();
    if (successCount > 0) setIsAddImagesOpen(false);
    return { success: successCount, fail: failCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, externalSaveToLibrary, board]);

  const handleAddExternalUrls = async () => {
    const validUrls = parsedUrls.filter((u) => u.valid).map((u) => u.url);
    if (validUrls.length === 0) return;

    try {
      const { data: dup } = await imagesAPI.checkDuplicates(boardId, { external_urls: validUrls });
      const dupItems = [];
      const uniqueUrls = [];
      for (const url of validUrls) {
        const existing = dup?.by_url?.[url];
        if (existing) {
          dupItems.push({
            key: url,
            label: url,
            url,
            existing,
          });
        } else {
          uniqueUrls.push(url);
        }
      }

      if (dupItems.length > 0) {
        setDupModal({
          items: dupItems,
          uniqueCount: uniqueUrls.length,
          proceed: async ({ addKeys, skipKeys }) => {
            const addSet = new Set(addKeys || []);
            const extras = dupItems.filter((d) => addSet.has(d.key)).map((d) => d.url);
            const result = await commitExternalUrlAdds([...uniqueUrls, ...extras]);
            resetExternalUrlInput();
            const skipped = (skipKeys || []).length;
            if (result.success > 0 && result.fail === 0) {
              const libMsg = externalSaveToLibrary ? ' and saved to library' : '';
              showToast(`${result.success} external image${result.success > 1 ? 's' : ''} added to board${libMsg}`);
            } else if (result.success > 0) {
              showToast(`Added ${result.success}, ${result.fail} failed`);
            } else if (skipped > 0) {
              showToast(`Skipped ${skipped} duplicate${skipped > 1 ? 's' : ''}`);
            }
          },
        });
        return;
      }
    } catch (err) {
      console.warn('External URL duplicate pre-check failed, continuing without it:', err);
    }

    const result = await commitExternalUrlAdds(validUrls);
    resetExternalUrlInput();
    if (result.success > 0 && result.fail === 0) {
      const libMsg = externalSaveToLibrary ? ' and saved to library' : '';
      showToast(`${result.success} external image${result.success > 1 ? 's' : ''} added to board${libMsg}`);
    } else if (result.success > 0 || result.fail > 0) {
      showToast(`Added ${result.success}, ${result.fail} failed`);
    }
  };

  const handleUpdateImage = async (imageId, updates) => {
    // Optimistic local merge — mirrors the handleSaveOwnerReply pattern.
    // On a 200-image board, the old approach of full `fetchBoard()` re-
    // downloads the whole payload and re-renders every card on every
    // section_label change. With the local merge + memoized cards, only
    // the touched card re-renders. fetchBoard is still called on error to
    // resync.
    setBoard((prev) => (prev ? ({
      ...prev,
      images: (prev.images || []).map((img) =>
        img.id === imageId ? { ...img, ...updates } : img
      ),
    }) : prev));
    try {
      await imagesAPI.update(boardId, imageId, updates);
    } catch (err) {
      console.error('Error updating image:', err);
      fetchBoard();
    }
  };

  const handleToggleFinal = async (imageId) => {
    // Optimistic flip — same reasoning as handleUpdateImage above.
    setBoard((prev) => (prev ? ({
      ...prev,
      images: (prev.images || []).map((img) =>
        img.id === imageId ? { ...img, is_final: !img.is_final } : img
      ),
    }) : prev));
    try {
      await imagesAPI.toggleFinal(boardId, imageId);
    } catch (err) {
      console.error('Error toggling final:', err);
      fetchBoard();
    }
  };

  /**
   * Save (or clear, when reply is empty) the owner's short note under a
   * specific image. Optimistically updates the in-memory board so the
   * inline reply textarea doesn't flicker back to the previous value
   * while the request is in-flight.
   */
  const handleSaveOwnerReply = async (imageId, reply) => {
    const trimmed = (reply ?? '').slice(0, 200);
    setBoard((prev) => (prev ? ({
      ...prev,
      images: (prev.images || []).map((img) =>
        img.id === imageId ? { ...img, owner_reply: trimmed } : img
      ),
    }) : prev));
    try {
      await imagesAPI.setImageOwnerReply(boardId, imageId, trimmed);
    } catch (err) {
      console.error('Error saving owner reply:', err);
      fetchBoard();
    }
  };

  const handleLockBoard = async () => {
    try {
      await boardsAPI.lock(boardId);
      fetchBoard();
      showToast('Board locked');
    } catch (err) {
      console.error('Error locking board:', err);
    }
  };

  const handleUnlockBoard = async () => {
    try {
      await boardsAPI.unlock(boardId);
      fetchBoard();
      showToast('Board unlocked');
    } catch (err) {
      console.error('Error unlocking board:', err);
    }
  };

  const handleArchiveBoard = async () => {
    try {
      await boardsAPI.archive(boardId);
      fetchBoard();
      showToast('Board archived');
    } catch (err) {
      console.error('Error archiving board:', err);
    }
  };

  const handleRestoreBoard = async () => {
    try {
      await boardsAPI.restore(boardId);
      fetchBoard();
      showToast('Board restored');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'board_limit_reached') {
        showToast("You've reached your current board limit.");
      } else {
        console.error('Error restoring board:', err);
      }
    }
  };

  const handleDeleteBoard = async () => {
    try {
      await boardsAPI.delete(boardId);
      setShowDeleteBoardModal(false);
      setDeletedBoardConfirmation(true);
    } catch (err) {
      console.error('Error deleting board:', err);
    }
  };

  const handleExtendBoard = async () => {
    setExtending(true);
    try {
      await boardsAPI.extend(boardId, extendDays);
      setShowExtendModal(false);
      fetchBoard();
      showToast(`Board reopened for ${extendDays} days`);
    } catch (err) {
      console.error('Error extending board:', err);
      showToast('Failed to extend board');
    } finally {
      setExtending(false);
    }
  };

  const isExpired = board && new Date(board.expires_at) < new Date();

  const handleUpdateSettings = async (updates) => {
    try {
      await boardsAPI.update(boardId, updates);
      fetchBoard();
      showToast('Settings saved');
    } catch (err) {
      console.error('Error updating settings:', err);
    }
  };

  const handleCreateLabelAndAssign = async (newLabel, imageId) => {
    try {
      const updatedLabels = [...sectionLabels, newLabel];
      setSectionLabels(updatedLabels);
      await labelsAPI.update(updatedLabels);
      await handleUpdateImage(imageId, { section_label: newLabel });
      fetchBoard();
      showToast(`Created label "${newLabel}"`);
    } catch (err) {
      console.error('Error creating label:', err);
      showToast('Failed to create label');
      setSectionLabels(sectionLabels);
    }
  };

  const handleCopyExportLink = async (text) => {
    await copyToClipboard(text);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 4000);
  };

  const handleExportPDF = async (options) => {
    setShowExportModal(false);
    setExporting(true);
    setExportProgress('running');
    try {
      const response = await boardsAPI.exportPDF(boardId, options);
      const token = response.data.download_token;
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/exports/download/${token}`;
      let size = null;
      try {
        const check = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/check/${token}`, {
          headers: { Authorization: `Bearer ${Cookies.get('kahla_token') || ''}` },
        });
        const info = await check.json();
        if (info.valid) size = info.size;
      } catch (e) { /* ignore check errors */ }
      // Flip the progress to "done" so the bar snaps to 100% and the
      // "Download starting" stage briefly shows; then hand off to the
      // existing download-ready dialog after a short editorial pause.
      setExportProgress('done');
      setTimeout(() => {
        setDownloadReady({ url: downloadUrl, filename: `${(brandName || 'Maestra').replace(/\s+/g, '_')}_Moodboard_${board.name}.pdf`, type: 'PDF', token, size });
        setExportProgress('idle');
      }, 700);
      if (archiveMode?.active) {
        boardsAPI.archiveExportStatus(boardId)
          .then(({ data: s }) => setBoardArchiveStatus(s)).catch(() => {});
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'archive_export_limit_reached') {
        // Quota errors are informational, not failures — close the
        // overlay quietly and surface the existing toast.
        setExportProgress('idle');
        showToast(detail.message || 'PDF export limit reached in archive mode.');
      } else {
        setExportProgress('failed');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleExportZip = async () => {
    setExportingZip(true);
    setExportProgress('running');
    try {
      const response = await boardsAPI.exportZip(boardId);
      const token = response.data.download_token;
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/exports/download/${token}`;
      let size = null;
      try {
        const check = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/check/${token}`, {
          headers: { Authorization: `Bearer ${Cookies.get('kahla_token') || ''}` },
        });
        const info = await check.json();
        if (info.valid) size = info.size;
      } catch (e) { /* ignore check errors */ }
      setExportProgress('done');
      setTimeout(() => {
        setDownloadReady({ url: downloadUrl, filename: `${(brandName || 'Maestra').replace(/\s+/g, '_')}_Backup_${board.name}.zip`, type: 'ZIP', token, size });
        setExportProgress('idle');
      }, 700);
      if (archiveMode?.active) {
        boardsAPI.archiveExportStatus(boardId)
          .then(({ data: s }) => setBoardArchiveStatus(s)).catch(() => {});
      }
    } catch (err) {
      console.error('Error exporting ZIP:', err);
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'archive_export_limit_reached') {
        setExportProgress('idle');
        showToast(detail.message || 'ZIP export limit reached in archive mode.');
      } else {
        setExportProgress('failed');
      }
    } finally {
      setExportingZip(false);
    }
  };

  // ── Library commit helper ──────────────────────────────────────────────
  const commitLibraryAdds = useCallback(async (libraryIds, sectionLabel) => {
    if (!libraryIds || libraryIds.length === 0) return;
    // Pre-compute the base once so concurrent Promise.all requests don't
    // race on the backend lookup. Same anchor pattern as file uploads.
    const existing = board?.images || [];
    const base = existing.length
      ? ((existing[existing.length - 1].order ?? existing.length - 1) + 1)
      : 0;
    try {
      await Promise.all(libraryIds.map((id, idx) =>
        imagesAPI.addFromLibrary(boardId, id, sectionLabel, base + idx)
      ));
      fetchBoard();
      setShowLibraryModal(false);
      setIsAddImagesOpen(false);
      showToast(libraryIds.length === 1 ? 'Image added from library' : `${libraryIds.length} images added from library`);
    } catch (err) {
      console.error('Error adding from library:', err);
      showToast('Failed to add images');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  const handleAddFromLibrary = async (libraryImageIds, sectionLabel) => {
    const ids = Array.isArray(libraryImageIds) ? libraryImageIds : [libraryImageIds];
    if (ids.length === 0) return;

    try {
      const { data: dup } = await imagesAPI.checkDuplicates(boardId, { library_image_ids: ids });
      const dupItems = [];
      const uniqueIds = [];
      for (const lid of ids) {
        const existing = dup?.by_library_id?.[lid];
        if (existing) {
          dupItems.push({
            key: lid,
            label: existing.existing_filename || '',
            libraryId: lid,
            existing,
          });
        } else {
          uniqueIds.push(lid);
        }
      }

      if (dupItems.length > 0) {
        setDupModal({
          items: dupItems,
          uniqueCount: uniqueIds.length,
          proceed: async ({ addKeys, skipKeys }) => {
            const addSet = new Set(addKeys || []);
            const extras = dupItems.filter((d) => addSet.has(d.key)).map((d) => d.libraryId);
            await commitLibraryAdds([...uniqueIds, ...extras], sectionLabel);
            const skipped = (skipKeys || []).length;
            if (skipped > 0 && extras.length === 0 && uniqueIds.length === 0) {
              showToast(`Skipped ${skipped} duplicate${skipped > 1 ? 's' : ''}`);
            }
          },
        });
        return;
      }
    } catch (err) {
      console.warn('Library duplicate pre-check failed, continuing without it:', err);
    }

    await commitLibraryAdds(ids, sectionLabel);
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/board/${board.share_token}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleRevealShareLink = () => {
    setShowShareLink(true);
    setTimeout(() => {
      const el = shareLinkInputRef.current;
      if (el) { el.focus(); el.select(); }
    }, 0);
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="board-editor" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!board) return null;

  const t = getTranslation(workspaceLang);

  return (
    <div className="board-editor fade-in">
      <ArchiveModeBanner archive={archiveMode} compact />

      <BoardTopbar
        t={t}
        navigate={navigate}
        board={board}
        brandName={brandName}
        workspaceLang={workspaceLang}
        boardArchiveStatus={boardArchiveStatus}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        onLockBoard={handleLockBoard}
        onUnlockBoard={handleUnlockBoard}
        isClientLinkOpen={isClientLinkOpen}
        setIsClientLinkOpen={setIsClientLinkOpen}
        showShareLink={showShareLink}
        setShowShareLink={setShowShareLink}
        shareLinkInputRef={shareLinkInputRef}
        linkCopied={linkCopied}
        copyShareLink={copyShareLink}
        handleRevealShareLink={handleRevealShareLink}
        setShowExtendModal={setShowExtendModal}
        isExpired={isExpired}
        featureAccess={featureAccess}
        showToast={showToast}
        boardsAPI={boardsAPI}
      />

      <div>
        {/* Main Content */}
        <div className="editor-content" style={{ paddingRight: showSettings ? 'calc(48px + 320px)' : '48px' }}>
          {/* Shared Client Space — editorial transition into the client experience */}
          <div
            data-testid="shared-client-space-divider"
            style={{
              marginTop: '8px',
              marginBottom: '32px',
              textAlign: 'center',
              padding: '0 24px',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: '64px',
                height: '1px',
                background: 'rgba(166,152,136,0.32)',
                margin: '0 auto 22px',
              }}
            />
            <p style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '10px',
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: 'var(--kahla-taupe)',
              margin: '0 0 8px',
              fontWeight: 500,
            }}>
              {t.shared_client_space}
            </p>
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontSize: '14px',
              fontStyle: 'italic',
              color: 'var(--kahla-text-light)',
              margin: 0,
              lineHeight: 1.5,
              maxWidth: '440px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              {t.shared_client_space_subtitle}
            </p>

            {/* Prominent "Customize Client View" CTA — the entry point
                into the new website-builder-style editor. Only shown on
                Client Boards for now (Inspiration boards can still open
                the editor via the same button, since Cover + Concept
                description live there too — inspiration surfaces are
                just as client-facing as client boards). */}
            <button
              type="button"
              data-testid="customize-client-view-btn"
              onClick={() => {
                navigate(`/board/${board.share_token}?edit=1`);
              }}
              style={{
                marginTop: '22px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 22px',
                background: 'var(--kahla-cream, #f6f1e7)',
                border: '1px solid #d8c597',
                cursor: 'pointer',
                fontFamily: 'Playfair Display, serif',
                fontSize: '12px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--kahla-charcoal)',
                fontWeight: 500,
                transition: 'background 200ms ease, transform 160ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eee2c6';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--kahla-cream, #f6f1e7)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span aria-hidden="true" style={{ fontSize: '14px' }}>✦</span>
              <span>{t.customize_client_view || 'Customize Client View'}</span>
            </button>
          </div>

          {/* "A note to your client" — moved here from the topbar so it
              sits inside the Shared Client Space and just above the
              Add Images flow, which is where the note is actually felt
              by the client. Same logic & data-testids, no functional
              change. The `board-top__note--standalone` modifier strips
              the original top border so the block doesn't feel
              orphaned now that it's no longer following a header
              metadata strip. */}
          {/* Custom Client Message — moved into the Client View Editor
              sidebar (Etap 3+). The panel used to live here as a
              collapsible "Note to your client" strip; it now sits with
              every other client-facing surface, editable through the
              same live-preview flow. */}

          <BoardAddImagesPanel
            t={t}
            isAddImagesOpen={isAddImagesOpen}
            setIsAddImagesOpen={setIsAddImagesOpen}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            uploading={uploading || uploadBatch.state.uploading}
            setShowLibraryModal={setShowLibraryModal}
            saveToLibrary={saveToLibrary}
            setSaveToLibrary={setSaveToLibrary}
            showExternalUrlInput={showExternalUrlInput}
            setShowExternalUrlInput={setShowExternalUrlInput}
            externalUrlText={externalUrlText}
            parsedUrls={parsedUrls}
            addingExternal={addingExternal}
            addProgress={addProgress}
            externalSaveToLibrary={externalSaveToLibrary}
            setExternalSaveToLibrary={setExternalSaveToLibrary}
            handleExternalUrlTextChange={handleExternalUrlTextChange}
            handleAddExternalUrls={handleAddExternalUrls}
            resetExternalUrlInput={resetExternalUrlInput}
          />

          {/* Batch upload progress / summary card. Renders nothing when idle. */}
          <BatchUploadStatus
            state={uploadBatch.state}
            label="image"
            onRetryFailed={uploadBatch.retryFailed}
            onDismiss={uploadBatch.dismiss}
          />

          <BoardImageGrid
            t={t}
            board={board}
            sectionLabels={sectionLabels}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            setLightboxIndex={setLightboxIndex}
            setShowLabelsModal={setShowLabelsModal}
            reorderMode={reorderMode}
            setReorderMode={setReorderMode}
            sensors={sensors}
            handleDragEnd={handleDragEnd}
            filterFinalOnly={filterFinalOnly}
            setFilterFinalOnly={setFilterFinalOnly}
            handleDeleteImage={handleDeleteImage}
            handleUpdateImage={handleUpdateImage}
            handleCreateLabelAndAssign={handleCreateLabelAndAssign}
            handleToggleFinal={handleToggleFinal}
            handleSaveOwnerReply={handleSaveOwnerReply}
            getReactionIcon={getReactionIcon}
            featureAccess={featureAccess}
            reactionMode={reactionMode}
            brandName={brandName}
            showFilenames={board.show_filenames === true}
            fetchBoard={fetchBoard}
            showToast={showToast}
          />

          {/* Client Inspirations — client-uploaded images. Hidden on
              Inspiration Boards (presentation-only surface). */}
          {board.inspirations?.length > 0 && board.board_type !== 'inspiration' && (
            <div className="inspiration-section">
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', marginBottom: '24px' }}>
                Client Inspirations
              </h2>
              <div className="inspiration-grid">
                {board.inspirations.map((insp, idx) => (
                  <div key={insp.id} className="inspiration-card" style={{ cursor: 'zoom-in' }} onClick={() => setInspirationLightboxIndex(idx)}>
                    <img src={insp.image_data || undefined} alt="" />
                    {insp.note && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '8px',
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        color: 'white',
                        fontSize: '11px',
                      }}>
                        {insp.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conversation + Documents — side by side on desktop (50/50),
              stacked on mobile, mirroring the Client View layout. */}
          {board.board_type !== 'inspiration' && (
            <div className={board.allow_notes !== false ? 'cv-bottom-grid' : undefined} data-testid="editor-bottom-grid">
              {board.allow_notes !== false && (
                <OwnerNotesSection
                  notes={board.notes || []}
                  boardId={boardId}
                  isExpired={board.current_status === 'EXPIRED'}
                  onNoteAdded={fetchBoard}
                  brandName={brandName}
                  clientName={board.client_name || ''}
                  participants={board.shared_decision_mode ? (board.shared_decision_participants || []) : []}
                  t={t}
                  documents={board.documents || []}
                  images={board.images || []}
                />
              )}
              <DocumentsSection
                documents={board.documents || []}
                boardId={boardId}
                isExpired={board.current_status === 'EXPIRED'}
                onDocumentsChanged={fetchBoard}
                t={t}
              />
            </div>
          )}

          {/* Private Space — owner-only notes / to-dos / gallery. The
              public client endpoint strips these fields, so nothing
              here can ever leak to the client view. */}
          <PrivateSpace
            board={board}
            boardId={boardId}
            t={t}
            onChanged={fetchBoard}
            showToast={showToast}
            lang={workspaceLang}
          />
        </div>

        <BoardSettingsPanel
          t={t}
          board={board}
          setBoard={setBoard}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          workspaceLang={workspaceLang}
          featureAccess={featureAccess}
          sectionLabels={sectionLabels}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          setShowLabelsModal={setShowLabelsModal}
          setShowDeleteBoardModal={setShowDeleteBoardModal}
          handleUpdateSettings={handleUpdateSettings}
          handleUpdateImage={handleUpdateImage}
          handleDeleteImage={handleDeleteImage}
          handleArchiveBoard={handleArchiveBoard}
          handleRestoreBoard={handleRestoreBoard}
          showToast={showToast}
          boardsAPI={boardsAPI}
          exporting={exporting}
          exportingZip={exportingZip}
          boardArchiveStatus={boardArchiveStatus}
          setShowExportModal={setShowExportModal}
          onExportZip={handleExportZip}
        />

      </div>

      {/* Library Modal */}
      {showLibraryModal && (
        <LibraryModal
          onClose={() => setShowLibraryModal(false)}
          onSelect={handleAddFromLibrary}
          sectionLabels={sectionLabels}
          lang={workspaceLang}
        />
      )}

      {/* Manage Labels Modal */}
      {showLabelsModal && (
        <ManageLabelsModal
          labels={sectionLabels}
          onSave={async (newLabels) => {
            try {
              const { data } = await labelsAPI.update(newLabels);
              setSectionLabels(data.labels);
              setShowLabelsModal(false);
              showToast('Labels updated');
            } catch (err) {
              console.error('Error saving labels:', err);
              showToast('Failed to save labels');
            }
          }}
          onClose={() => setShowLabelsModal(false)}
        />
      )}

      <DeleteBoardModal
        t={t}
        show={showDeleteBoardModal}
        onCancel={() => setShowDeleteBoardModal(false)}
        onConfirm={handleDeleteBoard}
      />

      <DeletedBoardConfirmation
        t={t}
        show={deletedBoardConfirmation}
        onDismiss={() => navigate('/boards')}
      />

      {/* Export PDF Modal */}
      {showExportModal && (
        <ExportPDFModal
          onClose={() => setShowExportModal(false)}
          onExport={handleExportPDF}
          t={t}
        />
      )}

      {/* Calm staged-progress overlay for both PDF + ZIP exports. */}
      <ExportProgressOverlay
        state={exportProgress}
        t={t}
        onDismiss={() => setExportProgress('idle')}
      />

      <DownloadReadyModal
        downloadReady={downloadReady}
        copiedLink={copiedLink}
        onCopyLink={handleCopyExportLink}
        onClose={() => { setDownloadReady(null); setCopiedLink(false); }}
        showToast={showToast}
      />

      {/* Image Lightbox */}
      {lightboxIndex !== null && board.images?.length > 0 && (
        <ImageLightbox
          images={board.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Demo Spotlight */}
      {(() => {
        const previewMode = typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('preview_onboarding') === '1';
        const shouldShow = previewMode || board.is_demo;
        if (!shouldShow) return null;
        return (
          <DemoSpotlight
            active={previewMode ? true : spotlightActive}
            forcePreview={previewMode}
            context="demo"
            boardType={board?.board_type || 'client'}
            onClose={() => {
              setSpotlightActive(false);
              if (!previewMode) {
                setDismissedGuidance((prev) => Array.from(new Set([...prev, 'demo_spotlight'])));
              }
            }}
          />
        );
      })()}

      {/* Inspiration Lightbox */}
      {inspirationLightboxIndex !== null && board.inspirations?.length > 0 && (
        <ImageLightbox
          images={board.inspirations.map((insp) => ({
            id: insp.id,
            image_full: insp.image_data,
            image_data: insp.image_data,
            image_thumb: insp.image_data,
            comment: insp.note || null,
            section_label: 'Client Inspiration',
          }))}
          initialIndex={inspirationLightboxIndex}
          onClose={() => setInspirationLightboxIndex(null)}
        />
      )}

      <ExtendBoardModal
        t={t}
        show={showExtendModal}
        isExpired={isExpired}
        workspaceLang={workspaceLang}
        extendDays={extendDays}
        setExtendDays={setExtendDays}
        extending={extending}
        onClose={() => setShowExtendModal(false)}
        onConfirm={handleExtendBoard}
      />

      <DuplicateConfirmModal
        open={!!dupModal}
        items={dupModal?.items || []}
        uniqueCount={dupModal?.uniqueCount || 0}
        onResolve={async (decision) => {
          const current = dupModal;
          setDupModal(null);
          if (current?.proceed) await current.proceed(decision);
        }}
        onCancel={async () => {
          // Backdrop / Esc — treat as "skip duplicates, keep uniques".
          const current = dupModal;
          setDupModal(null);
          if (current?.proceed) {
            await current.proceed({
              addKeys: [],
              skipKeys: (current.items || []).map((i) => i.key),
            });
          }
        }}
      />

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

export default BoardEditor;
